/**
 * Tutorial configuration.
 *
 * A professionally produced MP4/WebM can be dropped in later by:
 *   1. Setting REACT_APP_TUTORIAL_VIDEO_URL in the frontend .env, OR
 *   2. Updating `TUTORIAL_VIDEO_URL` below.
 *
 * When a URL is provided, TutorialModal renders an HTML5 <video>. When
 * empty, it falls back to the scene-based slideshow defined here — same
 * script, same narration, using the actual RendPleX pages/routes as
 * targets so nothing about the underlying app has to be rebuilt to
 * accommodate the tutorial.
 */

export const TUTORIAL_VIDEO_URL =
  process.env.REACT_APP_TUTORIAL_VIDEO_URL || '';

// Approximate total duration for the slideshow fallback (≈ 2m 40s).
// Each scene's duration is expressed in milliseconds.
export const TUTORIAL_SCENES = [
  {
    id: 'welcome',
    duration: 12000,
    icon: 'Sparkles',
    accent: 'from-[#12355B] to-[#1565C0]',
    titleKey: 'tutorial.scenes.welcome.title',
    subtitleKey: 'tutorial.scenes.welcome.subtitle',
    narrationKey: 'tutorial.scenes.welcome.narration',
  },
  {
    id: 'analyze',
    duration: 20000,
    icon: 'Plus',
    accent: 'from-[#45C04A] to-[#65D95A]',
    route: '/analyze',
    titleKey: 'tutorial.scenes.analyze.title',
    subtitleKey: 'tutorial.scenes.analyze.subtitle',
    narrationKey: 'tutorial.scenes.analyze.narration',
    bulletsKey: 'tutorial.scenes.analyze.bullets',
  },
  {
    id: 'generate',
    duration: 14000,
    icon: 'Zap',
    accent: 'from-[#1565C0] to-[#45C04A]',
    titleKey: 'tutorial.scenes.generate.title',
    subtitleKey: 'tutorial.scenes.generate.subtitle',
    narrationKey: 'tutorial.scenes.generate.narration',
  },
  {
    id: 'metrics',
    duration: 24000,
    icon: 'BarChart3',
    accent: 'from-[#12355B] to-[#45C04A]',
    titleKey: 'tutorial.scenes.metrics.title',
    subtitleKey: 'tutorial.scenes.metrics.subtitle',
    narrationKey: 'tutorial.scenes.metrics.narration',
    bulletsKey: 'tutorial.scenes.metrics.bullets',
  },
  {
    id: 'intelligence',
    duration: 16000,
    icon: 'Brain',
    accent: 'from-violet-500 to-indigo-600',
    titleKey: 'tutorial.scenes.intelligence.title',
    subtitleKey: 'tutorial.scenes.intelligence.subtitle',
    narrationKey: 'tutorial.scenes.intelligence.narration',
  },
  {
    id: 'scenario',
    duration: 22000,
    icon: 'Sliders',
    accent: 'from-[#45C04A] to-[#1565C0]',
    route: '/scenario',
    titleKey: 'tutorial.scenes.scenario.title',
    subtitleKey: 'tutorial.scenes.scenario.subtitle',
    narrationKey: 'tutorial.scenes.scenario.narration',
    bulletsKey: 'tutorial.scenes.scenario.bullets',
  },
  {
    id: 'save',
    duration: 12000,
    icon: 'Bookmark',
    accent: 'from-[#12355B] to-[#1565C0]',
    titleKey: 'tutorial.scenes.save.title',
    subtitleKey: 'tutorial.scenes.save.subtitle',
    narrationKey: 'tutorial.scenes.save.narration',
  },
  {
    id: 'advanced',
    duration: 18000,
    icon: 'Layers',
    accent: 'from-[#1565C0] to-[#12355B]',
    titleKey: 'tutorial.scenes.advanced.title',
    subtitleKey: 'tutorial.scenes.advanced.subtitle',
    narrationKey: 'tutorial.scenes.advanced.narration',
    bulletsKey: 'tutorial.scenes.advanced.bullets',
  },
  {
    id: 'ending',
    duration: 12000,
    icon: 'Rocket',
    accent: 'from-[#45C04A] to-[#65D95A]',
    titleKey: 'tutorial.scenes.ending.title',
    subtitleKey: 'tutorial.scenes.ending.subtitle',
    narrationKey: 'tutorial.scenes.ending.narration',
    isEnding: true,
  },
];

// localStorage keys — namespaced under rendplex_ to match existing app.
export const TUTORIAL_STORAGE = {
  COMPLETED: 'rendplex_tutorial_completed',
  DISMISSED: 'rendplex_tutorial_dismissed',
};
